<template>
  <AppLayout>
    <div class="user-detail-page">
      <div class="page-header">
        <div>
          <BaseButton variant="ghost" @click="router.back()">
            ← Retour
          </BaseButton>
          <h1>Détails de l'utilisateur</h1>
        </div>
        <div class="header-actions">
          <BaseButton variant="ghost" @click="editUser">
            ✏️ Modifier
          </BaseButton>
          <BaseButton 
            variant="primary" 
            @click="confirmDelete" 
            :disabled="user?.id === currentUserId"
          >
            🗑️ Supprimer
          </BaseButton>
        </div>
      </div>

      <div v-if="loading" class="loading">Chargement...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="user" class="user-detail">
        <BaseCard>
          <div class="detail-section">
            <h2>Informations générales</h2>
            <div class="detail-grid">
              <div class="detail-item">
                <label>Nom</label>
                <p>{{ user.nom }}</p>
              </div>
              <div class="detail-item">
                <label>Email</label>
                <p>{{ user.email }}</p>
              </div>
              <div class="detail-item">
                <label>Rôle</label>
                <BaseBadge :variant="getRoleVariant(user.role)">{{ user.role }}</BaseBadge>
              </div>
              <div class="detail-item">
                <label>Créé le</label>
                <p>{{ formatDate(user.creeLe) }}</p>
              </div>
            </div>
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Modal d'édition -->
    <Modal v-if="showEditModal" @close="closeEditModal">
      <template #title>Modifier utilisateur</template>
      <form @submit.prevent="handleUpdate" class="user-form">
        <BaseInput
          v-model.trim="userForm.nom"
          label="Nom"
          placeholder="Nom complet"
          required
        />
        <BaseInput
          v-model.trim="userForm.email"
          label="Email"
          type="email"
          placeholder="email@example.com"
          required
        />
        <BaseSelect
          v-model="userForm.role"
          label="Rôle"
          required
        >
          <option value="ADMIN">ADMIN</option>
          <option value="BUREAU">BUREAU</option>
          <option value="USINE">USINE</option>
          <option value="LOGISTIQUE">LOGISTIQUE</option>
          <option value="CHANTIER">CHANTIER</option>
          <option value="SUPERVISION">SUPERVISION</option>
        </BaseSelect>
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeEditModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="submitting">
            Enregistrer
          </BaseButton>
        </div>
      </form>
    </Modal>

    <!-- Modal de confirmation de suppression -->
    <Modal v-if="showDeleteModal" @close="showDeleteModal = false">
      <template #title>Confirmer la suppression</template>
      <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{{ user?.nom }}</strong> ?</p>
      <div class="form-actions">
        <BaseButton type="button" variant="ghost" @click="showDeleteModal = false">Annuler</BaseButton>
        <BaseButton variant="primary" @click="handleDelete" :loading="deleting">
          Supprimer
        </BaseButton>
      </div>
    </Modal>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseBadge from '../components/BaseBadge.vue';
import Modal from '../components/Modal.vue';
import { apiFetch } from '../utils/api';
import { useAuth } from '../composables/useAuth';

interface User {
  id: number;
  nom: string;
  email: string;
  role: string;
  creeLe: string;
}

const route = useRoute();
const router = useRouter();
const { user: currentUser } = useAuth();
const currentUserId = computed(() => currentUser.value?.id);

const user = ref<User | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const submitting = ref(false);
const deleting = ref(false);
const formError = ref<string | null>(null);

const userForm = reactive({
  nom: '',
  email: '',
  role: 'SUPERVISION'
});

onMounted(() => {
  loadUser();
});

async function loadUser() {
  const userId = parseInt(route.params.id as string, 10);
  if (!Number.isFinite(userId)) {
    error.value = 'ID utilisateur invalide';
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await apiFetch(`/authentification/users?limit=1&offset=0`);
    const data = response as { items: User[] };
    const foundUser = data.items.find(u => u.id === userId);
    if (!foundUser) {
      // Si pas trouvé dans la liste, essayer de récupérer directement
      // Pour l'instant, on utilise la liste
      error.value = 'Utilisateur non trouvé';
    } else {
      user.value = foundUser;
      userForm.nom = foundUser.nom;
      userForm.email = foundUser.email;
      userForm.role = foundUser.role;
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement';
  } finally {
    loading.value = false;
  }
}

function editUser() {
  if (user.value) {
    userForm.nom = user.value.nom;
    userForm.email = user.value.email;
    userForm.role = user.value.role;
    showEditModal.value = true;
  }
}

function confirmDelete() {
  showDeleteModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  formError.value = null;
  if (user.value) {
    userForm.nom = user.value.nom;
    userForm.email = user.value.email;
    userForm.role = user.value.role;
  }
}

async function handleUpdate() {
  if (!user.value) return;

  formError.value = null;
  submitting.value = true;

  try {
    await apiFetch(`/authentification/users/${user.value.id}`, {
      method: 'PUT',
      body: {
        nom: userForm.nom,
        email: userForm.email,
        role: userForm.role
      }
    });
    closeEditModal();
    loadUser();
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    submitting.value = false;
  }
}

async function handleDelete() {
  if (!user.value) return;

  deleting.value = true;
  try {
    await apiFetch(`/authentification/users/${user.value.id}`, {
      method: 'DELETE'
    });
    router.push({ name: 'users' });
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression';
  } finally {
    deleting.value = false;
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getRoleVariant(role: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (role) {
    case 'ADMIN':
      return 'error';
    case 'BUREAU':
      return 'primary';
    case 'USINE':
      return 'success';
    case 'LOGISTIQUE':
      return 'warning';
    default:
      return 'default';
  }
}
</script>

<style scoped>
.user-detail-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: var(--spacing-sm) 0 0 0;
}

.header-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.user-detail {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.detail-section {
  padding: var(--spacing-lg);
}

.detail-section h2 {
  margin: 0 0 var(--spacing-lg);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.detail-item label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item p {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  margin-bottom: var(--spacing-lg);
}

.loading {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--color-text-secondary);
}
</style>
